import { ShapeType } from 'components/Procedure/Header/SlideInteractionMenu/SlideAnnotationTools/options';
import {
  Point,
  Position,
} from 'components/Procedure/SlidesViewer/DeckGLViewer/layers/NebulaGLExtensions/geojson-types';
import { UndoStates } from 'services/annotations/annotationEditHistoryStore';
import { enumValuesList } from 'utils/enumValues';
import { Tagger } from './tagger';

export interface TodoOption {
  name: string; // the id of the class to annotate (e.g. necrosis)
  className?: string; // the type of annotation (e.g. polygon, point)
  shapeType?: ShapeType; // explicit geometry hint for annotation flows
  color?: string; // hex color code (e.g. #e6194b)
  displayName?: string;
  positive?: boolean;
  markerPositivity?: { [key: string]: number }; // key: stain type id, value: positivity cell rule
  isMarkerAnnotation?: boolean; // for markers annotations (can apply on top of an existing annotation, or as a new annotation with unknown diagnosis)
  deletedAt?: Date; // if the class was deleted
  createdAt?: Date;
  createdBy?: string;
  originPanelId?: string; // to track from which panel this option was created
}

export interface TodoDefinition {
  todo: string; // the name of the todo (e.g. registration)
  type: string; // type of the todo (e.g. annotation_marker)
  options: TodoOption[];
  todoTypesId?: number;
}

export type AnnotationPolygonCoordinates = Position[];

export type AnnotationFeaturePolygonGeometry = {
  type: 'Polygon';
  coordinates: AnnotationPolygonCoordinates;
};

export interface AnnotationFeature {
  type: string | number; // (e.g. Feature)
  geometry: AnnotationFeaturePolygonGeometry | Point;
  properties: AnnotationFeatureProperties;
  tags?: AnnotationFeatureProperties;
}

export interface AnnotationFeatureProperties {
  id: string;
  hidden: boolean;
  selected: boolean;
  diagnosis: string; // the id of the class this annotation is related to (corresponds to the TodoOption 'name' field, e.g. necrosis)
  markerType: string;
  shapeType: string;
  markerPositivity?: { [key: string]: number }; // key: stain type id, value: positivity cell rule
}

export interface AnnotationsData {
  todo: string; // the name of the todo (e.g. registration)
  markers: AnnotationFeature[];
}

export interface AnnotationAssignmentUpdate
  extends Pick<AnnotationAssignment, 'name' | 'todos' | 'isGlobal' | 'tags'> {}

export interface AnnotationAssignmentSummaryCount {
  value: string;
  count: number;
}

export interface AnnotationAssignmentSlideMetadataSummary {
  totalSlideCount: number;
  annotatedSlideCount: number;
  latestActivityAt?: string;
  stainTypeCounts: AnnotationAssignmentSummaryCount[];
  biopsySiteCounts: AnnotationAssignmentSummaryCount[];
}

export interface AnnotationAssignment {
  annotationAssignmentId: number;
  studyId?: string;
  name: string;
  todos: TodoDefinition[];
  isGlobal: boolean;
  description: string;
  allowedTaggerIds?: number[];
  archived: boolean;
  assignmentPriority: number;
  createdAt: Date;
  createdBy: string;
  taggerId?: number;
  slideIds?: string[];
  annotations?: Annotation[];
  tags?: string[];
  slideMetadataSummary?: AnnotationAssignmentSlideMetadataSummary;
}

export enum AnnotationWorkingStates {
  Annotating = 'annotating',
  QA = 'qa',
  Done = 'done',
}

export const AnnotationWorkingStateValues = enumValuesList(AnnotationWorkingStates);

export interface AnnotationSummary {
  [key: string]: number | { positive?: number; negative?: number };
} // key: class/marker, value: count or object with 'positive'/'negative' counts

export interface Annotation {
  annotationId: number;
  slideId: string;
  annotationAssignment: AnnotationAssignment;
  createdAt: string;
  annotationsData?: AnnotationsData[]; // last saved version (annotations table)
  // this is data taken from the annotation_extensions table, representing the unpublished annotations
  draftAnnotationsData?: AnnotationsData[];
  // this is calculation happening on the back end, to not fetch all annotationsData
  annotationSummary?: AnnotationSummary;
  draftAnnotationSummary?: AnnotationSummary;
  taggerId: number;
  tagger?: Tagger;
  draftModifiedAt?: string;
  draftVersionHash?: string;
  validFrom: string;
  validTo: string;
  workingState: AnnotationWorkingStates;
  participantTaggerId: number;
  history?: UndoStates; // in the db its saved under the annotation_extensions table, but we combine it when fetching
}

export interface AnnotationDraftVersionResponse {
  draftVersionHash?: string | null;
}

export interface BulkAnnotationWorkingStateSlideRequest {
  slideId: string;
  draftVersionHash?: string;
}

export interface BulkAnnotationWorkingStateRequest {
  workingState: AnnotationWorkingStates;
  slides: BulkAnnotationWorkingStateSlideRequest[];
}

export interface BulkAnnotationWorkingStateResponse {
  updatedSlideIds: string[];
  unchangedSlideIds: string[];
}

export interface AnnotationVersion {
  annotationVersionId: number;
  annotationId: number;
  slideId: string;
  annotationAssignmentId: number;
  createdAt: string;
  annotationsData?: AnnotationsData[];
  annotationSummary?: AnnotationSummary;
  versionHash?: string | null;
  taggerId?: number;
  workingState: AnnotationWorkingStates;
  validFrom: string;
  validTo: string;
  participantTaggerId?: number;
  tagger?: Tagger;
  isCurrentVersion: boolean;
}

export enum AnnotationExtensionStates {
  Annotating = 'annotating',
  QA = 'qa',
  Done = 'done',
  None = 'none',
  AllAssignment = 'all',
  StarredAssignment = 'starred',
}

export interface AnnotationExtension {
  annotationAssignmentId: number;
  annotationExtensionsId: number;
  slideId: string;
  extensionAnnotationsData?: AnnotationsData[];
  taggerId: number;
  workingState: AnnotationExtensionStates;
  extensionPriority: number;
  url: string;
}

export enum ClassRemovalAction {
  keep = 'keep',
  replace = 'replace',
  delete = 'delete',
}

export interface AnnotationClassRemovalOperation {
  classToRemove: TodoOption;
  action: ClassRemovalAction;
  classToReplaceWith?: TodoOption;
}

export function generateAnnotationExtensionSlideAssignmentId(assignmentId: number, slideId: string) {
  return `${slideId}||${assignmentId}`;
}
